/**
 * The session's editor-only content (trace-viewer CONTENT_MODEL_SPEC §2.9,
 * §2.10): one editor root under the document root, and under it one
 * `AiSession` node per AI conversation, its `src` the session file.
 *
 * None of it may reach a published scene, so every writer that captures the
 * whole scene spreads {@link editorExclusion} into its options. A `.glz`
 * download packs it as `editor/` ({@link exportGlz}); opening or importing a
 * `.glz` mounts it back ({@link adoptEditorBundle}).
 *
 * The root and the session nodes are created outside the undo stack: undoing
 * an AI edit never takes its record with it. The root is mounted in the scene
 * frame (`mountEditorRoot`), so its pose is not the document root's and is
 * not editable.
 */

import type { TraceViewerElement } from 'trace-viewer';
import type { BundleExportOptions, SceneObject, TraceScene } from 'trace-viewer/scene';
import { AiSession } from 'trace-viewer/behaviors';
import { mountEditorRoot, packEditorBundle, readEditorBundle } from 'trace-viewer/editor';
import { parseSessionFile, sessionTitle, type AiSessionFile } from './ai/session-file';

/** The editor root's name. Only a label: the root is known by identity. */
export const EDITOR_ROOT_NAME = 'Editor';

let editorRoot: SceneObject | null = null;
/** The Export menu's "Include AI history and references": on by default (spec §4). */
let includeInDownloads = true;
/**
 * Session id → its node, for the conversations this session has written or
 * loaded. Module state like the root, dropped with it when the scene changes.
 */
const sessionNodes = new Map<string, SceneObject>();

/** The editor root, if it is still part of `scene`. A new scene drops it. */
function liveRoot(scene: TraceScene): SceneObject | null {
    if (editorRoot && !scene.getSceneObjects().includes(editorRoot)) {
        editorRoot = null;
        sessionNodes.clear();
    }
    return editorRoot;
}

/** Writer options that leave the editor root out of a whole-scene capture. */
export function editorExclusion(scene: TraceScene | null | undefined): { exclude?: SceneObject[] } {
    const root = scene ? liveRoot(scene) : null;
    return root ? { exclude: [root] } : {};
}

/**
 * {@link editorExclusion} for a subtree export: the same cut, except when the
 * subtree IS the editor root, which would exclude the whole capture.
 */
export function subtreeExclusion(scene: TraceScene | null | undefined, subtree: SceneObject): { exclude?: SceneObject[] } {
    return scene && subtree === liveRoot(scene) ? {} : editorExclusion(scene);
}

/**
 * Whether a capture of `subtree` would carry the editor root: it is the root
 * or holds it. For writers that take no exclusion (`.glb`), which must refuse.
 */
export function includesEditorRoot(scene: TraceScene | null | undefined, subtree: SceneObject): boolean {
    const root = scene ? liveRoot(scene) : null;
    return !!root && (subtree === root || subtree.getDescendants().includes(root));
}

async function ensureEditorRoot(scene: TraceScene): Promise<SceneObject> {
    const existing = liveRoot(scene);
    if (existing) return existing;
    const documentRoot = await scene.ensureDocumentRoot();
    editorRoot = await scene.createSceneObject(EDITOR_ROOT_NAME, documentRoot);
    mountEditorRoot(editorRoot, documentRoot);
    return editorRoot;
}

function sessionBlob(file: AiSessionFile): Blob {
    return new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' });
}

/**
 * Write `file` into the scene: its node's bytes are replaced, or a node is
 * made for a conversation the scene has not held before.
 */
export async function saveSessionToScene(scene: TraceScene, file: AiSessionFile): Promise<void> {
    const root = await ensureEditorRoot(scene);
    const node = sessionNodes.get(file.id);
    if (node?.src && scene.getSceneObjects().includes(node)) {
        scene.resources.addAsset(node.src.replace('./', ''), sessionBlob(file));
        return;
    }
    const key = `assets/ai-session-${file.id.slice(0, 8)}.json`;
    scene.resources.addAsset(key, sessionBlob(file));
    const created = await scene.createSceneObject(sessionTitle(file), root, `./${key}`);
    await created.addBehavior(AiSession);
    sessionNodes.set(file.id, created);
}

export function setIncludeEditorContentInDownloads(include: boolean): void {
    includeInDownloads = include;
}

/**
 * The scene as a downloaded `.glz`: with `editor/` when the scene holds editor
 * content and the download choice is on, else the scene alone.
 */
export async function exportGlz(
    viewer: TraceViewerElement,
    onProgress?: BundleExportOptions['onProgress'],
): Promise<Blob> {
    const scene = viewer.traceScene;
    const root = scene ? liveRoot(scene) : null;
    const sceneBundle = await viewer.exportToGlzBlob({ ...editorExclusion(scene), onProgress });
    if (!includeInDownloads || !root) return sceneBundle;
    return packEditorBundle(sceneBundle, await viewer.exportSubtreeToGlzBlob(root));
}

/**
 * Mount the `editor/` folder of an archive just opened or imported, and return
 * its newest conversation, if any.
 *
 * Imported into a scene that already has an editor root, the archive's layers
 * join it, so one scene keeps one root. A conversation the scene already holds
 * (the same id) is kept once: whichever copy was updated last.
 */
export async function adoptEditorBundle(scene: TraceScene, archive: Blob): Promise<AiSessionFile | null> {
    const bundle = await readEditorBundle(archive);
    if (!bundle) return null;
    const documentRoot = await scene.ensureDocumentRoot();
    const attached = await scene.attachDocumentFromBlob(bundle, 'editor.glz', documentRoot);
    if (!attached) return null;

    const existing = liveRoot(scene);
    const layers = attached.getChildren();
    let newest: AiSessionFile | null = null;
    for (const layer of layers) {
        const file = await readSession(scene, layer);
        if (!file) continue;
        const held = sessionNodes.get(file.id);
        if (held && held !== layer && scene.getSceneObjects().includes(held)) {
            const heldFile = await readSession(scene, held);
            if (heldFile && heldFile.updatedAt >= file.updatedAt) {
                scene.deleteSceneObject(layer);
                continue;
            }
            scene.deleteSceneObject(held);
        }
        sessionNodes.set(file.id, layer);
        if (!newest || file.updatedAt > newest.updatedAt) newest = file;
    }

    if (existing) {
        for (const layer of attached.getChildren()) scene.reparentSceneObject(layer, existing);
        scene.deleteSceneObject(attached);
    } else {
        mountEditorRoot(attached, documentRoot);
        editorRoot = attached;
    }
    return newest;
}

/** The conversation `node` carries, or null when it is not one (or unreadable). */
async function readSession(scene: TraceScene, node: SceneObject): Promise<AiSessionFile | null> {
    if (!node.getBehavior(AiSession) || !node.src) return null;
    const blob = scene.resources.getAsset(node.src);
    if (!blob) return null;
    try {
        return parseSessionFile(await blob.text());
    } catch (err) {
        console.warn(`[AI] Skipping unreadable conversation "${node.name}":`, err);
        return null;
    }
}

